import React, { useState, useMemo } from "react";
import { sendPortalMessage, sendToEhpad } from "../services/api";
import EmailAutocomplete from "./EmailAutocomplete";
import { mailErrorMessage } from "../utils/mailErrorMessage";
import { generateUuid } from "../utils/clientId";
import { isPortalConversation } from "../utils/commandeMail";

export default function SendToEhpadModal({ commande, onClose, initialSujet, initialContenu }) {
    const idempotencyKey = useMemo(() => generateUuid(), []);
    const portalConversation = isPortalConversation(commande);

    const initialRecipients = commande.ehpad?.emailsContact ?? [];
    const [recipients, setRecipients] = useState([...initialRecipients]);
    const [sujet, setSujet] = useState(initialSujet ?? `Re: ${commande.commentaire || ""}`);
    const [contenu, setContenu] = useState(initialContenu ?? "");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    const addRecipient = (email) => {
        if (!email || recipients.includes(email)) return;
        setRecipients(prev => [...prev, email]);
    };

    const removeRecipient = (index) => {
        setRecipients(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileChange = (e) => {
        setFiles(prev => [...prev, ...Array.from(e.target.files)]);
        e.target.value = "";
    };

    const handleSend = async () => {
        if (!contenu.trim() || (!portalConversation && (!sujet.trim() || recipients.length === 0))) return;
        setSending(true);
        setError(null);
        try {
            if (portalConversation) {
                await sendPortalMessage(commande.id, contenu, idempotencyKey);
            } else {
                await sendToEhpad(commande.id, sujet, contenu, recipients, files, idempotencyKey);
            }
            onClose(true);
        } catch (e) {
            const message = mailErrorMessage(e);
            setError(portalConversation && message === "Erreur lors de l'envoi du mail."
                ? "Erreur lors de l'envoi du message."
                : message);
            setSending(false);
        }
    };

    return (
        <div style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000
        }}>
            <div style={{
                background: "white",
                borderRadius: "12px",
                width: "580px",
                maxWidth: "95vw",
                maxHeight: "90vh",
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}>
                {/* Header */}
                <div style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                    <span style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
                        Écrire à l'EHPAD
                        {commande.ehpadNom && (
                            <span style={{ fontWeight: 400, color: "#64748b", marginLeft: "6px" }}>
                                — {commande.ehpadNom}
                            </span>
                        )}
                    </span>
                    <button onClick={() => onClose(false)} style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: "18px", color: "#94a3b8", lineHeight: 1
                    }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "12px", overflowY: "auto" }}>

                    {portalConversation && (
                        <div style={{ padding: "10px 12px", borderRadius: "7px", background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", fontSize: "12px" }}>
                            Ce message sera publié dans la conversation du portail EHPAD.
                        </div>
                    )}

                    {/* Destinataires */}
                    {!portalConversation && <div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>
                            Destinataires *
                        </div>
                        <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            alignItems: "center",
                            border: "1px solid #cbd5e1",
                            borderRadius: "7px",
                            padding: "6px 8px",
                            minHeight: "40px",
                        }}>
                            {recipients.map((r, i) => (
                                <span key={i} style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    background: "#eff6ff",
                                    border: "1px solid #bfdbfe",
                                    borderRadius: "4px",
                                    padding: "2px 6px",
                                    fontSize: "12px",
                                    color: "#1e40af",
                                }}>
                                    {r}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); removeRecipient(i); }}
                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#93c5fd", fontSize: "14px", lineHeight: 1, padding: 0 }}
                                    >×</button>
                                </span>
                            ))}
                            <EmailAutocomplete
                                onSelect={addRecipient}
                                exclude={recipients}
                                placeholder={recipients.length === 0 ? "Ajouter un destinataire..." : ""}
                            />
                        </div>
                        {recipients.length === 0 && (
                            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#f59e0b" }}>
                                Aucun destinataire — cet EHPAD n'a peut-être pas d'emails contact configurés.
                            </p>
                        )}
                    </div>}

                    {/* Sujet */}
                    {!portalConversation && <input
                        value={sujet}
                        onChange={e => setSujet(e.target.value)}
                        placeholder="Objet"
                        style={{
                            padding: "8px 12px",
                            borderRadius: "7px",
                            border: "1px solid #cbd5e1",
                            fontSize: "13px",
                            outline: "none",
                            color: "#1e293b"
                        }}
                    />}

                    {/* Corps */}
                    <textarea
                        value={contenu}
                        onChange={e => setContenu(e.target.value)}
                        placeholder="Votre message..."
                        rows={8}
                        style={{
                            padding: "10px 12px",
                            borderRadius: "7px",
                            border: "1px solid #cbd5e1",
                            fontSize: "13px",
                            resize: "vertical",
                            outline: "none",
                            color: "#1e293b",
                            lineHeight: "1.6",
                            fontFamily: "inherit"
                        }}
                    />

                    {/* Pièces jointes */}
                    {!portalConversation && <div>
                        <label style={{
                            display: "inline-block",
                            padding: "5px 12px",
                            borderRadius: "7px",
                            border: "1px dashed #93c5fd",
                            background: "#f0f9ff",
                            color: "#2563eb",
                            fontSize: "12px",
                            cursor: "pointer"
                        }}>
                            <span>+ Ajouter une pièce jointe</span>
                            <input type="file" multiple style={{ display: "none" }} onChange={handleFileChange} />
                        </label>
                        {files.length > 0 && (
                            <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
                                {files.map((f, i) => (
                                    <div key={i} style={{
                                        display: "flex", alignItems: "center", gap: "8px",
                                        padding: "6px 10px",
                                        background: "#f0fdf4",
                                        border: "1px solid #bbf7d0",
                                        borderRadius: "7px",
                                        fontSize: "12px"
                                    }}>
                                        <span style={{ color: "#16a34a", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {f.name}
                                        </span>
                                        <button
                                            aria-label={`Retirer ${f.name}`}
                                            onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                                            style={{ background: "none", border: "none", cursor: "pointer", color: "#86efac", fontSize: "14px", padding: 0 }}
                                        >✕</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>}

                    {error && <div style={{ color: "#dc2626", fontSize: "13px" }}>{error}</div>}
                </div>

                {/* Footer */}
                <div style={{
                    padding: "12px 20px",
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px"
                }}>
                    <button
                        onClick={() => onClose(false)}
                        disabled={sending}
                        style={{
                            padding: "7px 16px",
                            borderRadius: "7px",
                            border: "1px solid #cbd5e1",
                            background: "white",
                            color: "#475569",
                            fontSize: "13px",
                            cursor: "pointer"
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={sending || !contenu.trim() || (!portalConversation && (!sujet.trim() || recipients.length === 0))}
                        style={{
                            padding: "7px 16px",
                            borderRadius: "7px",
                            border: "none",
                            background: sending || (!portalConversation && recipients.length === 0) ? "#93c5fd" : "#2563eb",
                            color: "white",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: (sending || (!portalConversation && recipients.length === 0)) ? "not-allowed" : "pointer"
                        }}
                    >
                        {sending ? "Envoi..." : (portalConversation ? "Envoyer" : `Envoyer (${recipients.length})`)}
                    </button>
                </div>
            </div>
        </div>
    );
}
